// Pure regroup step for a render spec that declares a drill-down.
//
// Its own module, with no imports, so it can be exercised without standing up
// the runner's browser-side collaborators.

// Unwrap a step output into the rows a render consumes.
//
// An analytics step yields `{ columns, rows }`; a Python step yields whatever it
// assigned. Both reach a render, so the shape is normalized in one place.
export function extractRenderData(stepOutput) {
    if (stepOutput == null) return null;
    if (stepOutput && typeof stepOutput === "object" && Array.isArray(stepOutput.rows)) {
        return stepOutput.rows;
    }
    return stepOutput;
}
// Nest a second step's rows under the render rows they belong to.
//
// A drill-down needs one query for the parent's aggregates and another for the
// rows behind them, and nothing between the two could join them: the step kinds
// are `analytics_query`, `python`, `mcp_tool` and `agent`, so the only in-kernel
// option was Python. That is not available everywhere -- the isolated realm
// needs `VITE_PYTHON_REALM_ORIGIN`, which local development does not set -- and
// a built-in canvas panel that renders nothing without it is not a panel.
//
// So the join happens here, where every step's output is already in hand. It
// stays a pure regroup: the parent rows keep the aggregates their own query
// computed server-side, and this only decides which detail rows hang beneath
// each one. Folding flat rows into aggregates would be the thing to avoid,
// because a cap the server already applied would silently reshape the totals.
export function attachDetailRows(rows, spec, outputs) {
    const source = typeof spec?.detailSource === "string" ? spec.detailSource : null;
    const field = typeof spec?.detailField === "string" ? spec.detailField : null;
    const key = typeof spec?.detailKey === "string" ? spec.detailKey : null;
    if (!source || !field || !key || !Array.isArray(rows)) return rows;
    const detail = extractRenderData(outputs.get(source));
    if (!Array.isArray(detail)) return rows;

    const grouped = new Map();
    for (const child of detail) {
        if (!child || typeof child !== "object") continue;
        const owner = child[key];
        if (owner === undefined || owner === null) continue;
        const bucket = grouped.get(owner);
        if (bucket) bucket.push(child);
        else grouped.set(owner, [child]);
    }
    return rows.map((row) => (
        row && typeof row === "object"
            ? { ...row, [field]: grouped.get(row[key]) ?? [] }
            : row
    ));
}